import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { ActionItem, GaRef, DecisionValue } from './lib/types';
import { PRIORITY_STYLE } from './lib/selectors';
import { ACCENT, BURGUNDY, FONT, RADIUS, TONE, accentText } from './lib/brand';

const mono = FONT.mono;

export function PriorityChip({ priority, score, bucket }: { priority: string; score?: number | null; bucket?: string | null }) {
  const st = PRIORITY_STYLE[priority] || PRIORITY_STYLE.Unscored;
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          fontFamily: mono, fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase',
          px: 0.9, py: 0.2, borderRadius: RADIUS.sm, bgcolor: st.bg, color: st.fg,
          ...(st.border ? { border: `1px solid ${st.border}` } : {}),
        }}
      >
        {priority}
      </Box>
      {(score != null || bucket) && (
        <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
          {score != null ? `score ${score}` : ''}{score != null && bucket ? ' · ' : ''}{bucket || ''}
        </Box>
      )}
    </Box>
  );
}

/** "CFO · ⇄ Alternative" (amber) or "CMO · ✓ Agrees" (green). Icon + text, never colour alone. */
export function VerdictChip({ exec, verdict, onClick }: { exec: string; verdict: 'agree' | 'alternative'; onClick?: () => void }) {
  const alt = verdict === 'alternative';
  const sx = {
    display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 11, fontWeight: 600, px: 1, py: 0.25,
    borderRadius: RADIUS.pill, border: '1px solid', whiteSpace: 'nowrap' as const,
    color: alt ? TONE.warn.fg : TONE.good.fg, bgcolor: alt ? TONE.warn.bg : TONE.good.bg, borderColor: alt ? TONE.warn.border : TONE.good.border,
  };
  const text = `${exec} · ${alt ? '⇄ Alternative' : '✓ Agrees'}`;
  return onClick
    ? <ButtonBase onClick={onClick} sx={sx} aria-label={`${text}. Open the recommendation`}>{text}</ButtonBase>
    : <Box component="span" sx={sx}>{text}</Box>;
}

export function NoReviewChip({ level = 'bot' }: { level?: 'bot' | 'item' }) {
  return (
    <Box component="span" sx={{ display: 'inline-block', fontSize: 11, fontWeight: 600, px: 1, py: 0.25, borderRadius: RADIUS.pill, color: TONE.neutral.fg, bgcolor: TONE.neutral.bg, border: `1px solid ${TONE.neutral.border}` }}>
      {level === 'bot' ? 'No C-suite review yet' : 'No C-suite review of this item'}
    </Box>
  );
}

/** Where the recommended action comes from: the exec's alternative (amber) or the bot (accent). */
export function RecommendedLabel({ action, accent }: { action: ActionItem; accent: string }) {
  const fromExec = action.recommended_by === 'exec';
  const label = action.recommended_label || (fromExec ? 'C-suite alternative' : 'Bot recommendation');
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', fontSize: 11, fontWeight: 600, px: 1, py: 0.25, borderRadius: RADIUS.pill, border: '1px solid',
        ...(fromExec
          ? { color: TONE.warn.fg, bgcolor: TONE.warn.bg, borderColor: TONE.warn.border }
          : { color: accentText(accent), bgcolor: 'transparent', borderColor: accent }),
      }}
    >
      {fromExec ? '⇄ ' : ''}{label}
    </Box>
  );
}

const STATE: Record<DecisionValue, { text: string; fg: string; bg: string } | null> = {
  pending: null,
  yes: { text: '✓ Approved → Queued for implementation', fg: TONE.good.fg, bg: TONE.good.bg },
  no: { text: '✕ Declined', fg: TONE.neutral.fg, bg: TONE.neutral.bg },
  instructed: { text: '✎ Instructed instead', fg: ACCENT.lifescribe.ink, bg: ACCENT.lifescribe.soft },
};

export function DecisionStateChip({ value, onClick }: { value: DecisionValue; onClick?: () => void }) {
  const st = STATE[value];
  if (!st) return null;
  const sx = { display: 'inline-block', fontSize: 11.5, fontWeight: 600, px: 1.1, py: 0.4, borderRadius: RADIUS.pill, color: st.fg, bgcolor: st.bg };
  return onClick
    ? <ButtonBase onClick={onClick} sx={sx}>{st.text}</ButtonBase>
    : <Box component="span" sx={sx}>{st.text}</Box>;
}

/** A Grant-ask chip: "GA-30 · OPEN". Opens and scrolls to that row of the Grant asks panel. */
export function GaChip({ ga, onClick }: { ga: GaRef; onClick: (id: string) => void }) {
  const open = (ga.status || '').toUpperCase().startsWith('OPEN');
  return (
    <ButtonBase
      onClick={(e) => { e.stopPropagation(); onClick(ga.id); }}
      title={ga.title || ga.id}
      aria-label={`${ga.id}${ga.status ? `, ${ga.status}` : ''}. Open in Grant asks`}
      sx={{
        fontFamily: mono, fontSize: 10.5, fontWeight: 600, px: 0.9, py: 0.2, borderRadius: RADIUS.sm,
        color: open ? TONE.warn.fg : TONE.neutral.fg, bgcolor: open ? TONE.warn.bg : TONE.neutral.bg, border: `1px solid ${open ? TONE.warn.border : TONE.neutral.border}`,
      }}
    >
      {ga.id}{ga.status ? ` · ${ga.status}` : ''}
    </ButtonBase>
  );
}

export function UnreadBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <Box
      component="span"
      aria-label={`${count} unread`}
      sx={{ fontFamily: mono, fontSize: 10, fontWeight: 700, minWidth: 17, height: 17, px: 0.6, borderRadius: RADIUS.pill, bgcolor: BURGUNDY, color: TONE.strong.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {count}
    </Box>
  );
}

/** A provenance line: "path:line § heading · Oct 5 · 17:32 ICT". Nothing is invented. */
export function ProvLine({ prov, fmt }: { prov?: { path: string; line?: number | null; heading?: string | null; field?: string | null; mtime_ict?: string | null } | null; fmt: (iso?: string | null) => string }) {
  if (!prov) return <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>Not stated in source</Box>;
  const where = `${prov.path}${prov.line ? `:${prov.line}` : ''}${prov.heading ? ` § ${prov.heading}` : ''}${prov.field ? ` · ${prov.field}` : ''}`;
  return (
    <Box component="span" sx={{ fontFamily: mono, fontSize: 10.5, lineHeight: 1.5, display: 'block', color: 'text.secondary', wordBreak: 'break-all' }}>
      {where}{prov.mtime_ict ? ` · ${fmt(prov.mtime_ict)}` : ''}
    </Box>
  );
}
